import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { useAuth } from "@/auth/AuthProvider";
import { api } from "@/lib/api";

type Supplier = {
  id: number;
  name: string;
  tax_code: string | null;
  phone: string | null;
  address: string | null;
  email: string | null;
  note: string | null;
  active: boolean;
};

const emptyForm = (): Partial<Supplier> => ({
  name: "",
  tax_code: "",
  phone: "",
  address: "",
  email: "",
  note: "",
  active: true,
});

export function SuppliersPage() {
  const { user } = useAuth();
  const canWrite = user?.role === "admin" || user?.role === "ketoan";
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Partial<Supplier> | null>(null);

  const listQ = useQuery({
    queryKey: ["suppliers", search],
    queryFn: async () =>
      (await api.get<Supplier[]>("/suppliers", { params: { q: search || undefined } })).data,
  });

  const save = useMutation({
    mutationFn: async (s: Partial<Supplier>) => {
      if (s.id) {
        const { id, ...rest } = s;
        return (await api.patch<Supplier>(`/suppliers/${id}`, rest)).data;
      }
      return (await api.post<Supplier>("/suppliers", s)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["suppliers"] });
      setEditing(null);
    },
  });

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Nhà cung cấp</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Danh sách NCC hiện có, auto-tạo từ hoá đơn đầu vào.
          </p>
        </div>
        {canWrite && (
          <button
            onClick={() => setEditing(emptyForm())}
            className="btn-primary"
          >
            + Thêm NCC
          </button>
        )}
      </div>

      <div className="mb-3">
        <input
          placeholder="Tìm theo tên / MST / SĐT..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border rounded px-3 py-1.5 text-sm w-full max-w-md"
        />
      </div>

      <table className="w-full bg-white border border-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="text-left px-3 py-2">Tên</th>
            <th className="text-left px-3 py-2">MST</th>
            <th className="text-left px-3 py-2">SĐT</th>
            <th className="text-left px-3 py-2">Email</th>
            <th className="text-left px-3 py-2">Địa chỉ</th>
            <th className="text-center px-3 py-2">Hoạt động</th>
          </tr>
        </thead>
        <tbody>
          {listQ.isLoading && (
            <tr>
              <td colSpan={6} className="px-3 py-6 text-center text-gray-500">
                Đang tải...
              </td>
            </tr>
          )}
          {listQ.data?.length === 0 && (
            <tr>
              <td colSpan={6} className="px-3 py-6 text-center text-gray-500">
                Chưa có nhà cung cấp.
              </td>
            </tr>
          )}
          {listQ.data?.map((s) => (
            <tr
              key={s.id}
              className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
              onClick={() => canWrite && setEditing(s)}
            >
              <td className="px-3 py-2 font-medium">{s.name}</td>
              <td className="px-3 py-2 font-mono text-xs">{s.tax_code ?? "—"}</td>
              <td className="px-3 py-2">{s.phone ?? "—"}</td>
              <td className="px-3 py-2 text-gray-600">{s.email ?? "—"}</td>
              <td className="px-3 py-2 text-gray-600 truncate max-w-[20rem]">
                {s.address ?? "—"}
              </td>
              <td className="px-3 py-2 text-center">{s.active ? "✓" : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {editing && (
        <EditDrawer
          form={editing}
          onClose={() => setEditing(null)}
          onSave={(f) => save.mutate(f)}
          saving={save.isPending}
          error={save.error as any}
        />
      )}
    </div>
  );
}

function EditDrawer({
  form: initial,
  onClose,
  onSave,
  saving,
  error,
}: {
  form: Partial<Supplier>;
  onClose: () => void;
  onSave: (f: Partial<Supplier>) => void;
  saving: boolean;
  error: any;
}) {
  const [form, setForm] = useState(initial);
  useEffect(() => setForm(initial), [initial]);

  return (
    <div className="fixed inset-0 bg-black/30 flex justify-end z-50">
      <div className="w-full max-w-lg bg-white h-full overflow-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">
            {form.id ? "Sửa nhà cung cấp" : "Thêm nhà cung cấp"}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900">
            ✕
          </button>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSave(form);
          }}
          className="space-y-3"
        >
          <Field label="Tên *" required value={form.name ?? ""} onChange={(v) => setForm({ ...form, name: v })} />
          <Field label="MST" value={form.tax_code ?? ""} onChange={(v) => setForm({ ...form, tax_code: v })} />
          <Field label="SĐT" value={form.phone ?? ""} onChange={(v) => setForm({ ...form, phone: v })} />
          <Field label="Email" value={form.email ?? ""} onChange={(v) => setForm({ ...form, email: v })} />
          <Field label="Địa chỉ" textarea value={form.address ?? ""} onChange={(v) => setForm({ ...form, address: v })} />
          <Field label="Ghi chú" textarea value={form.note ?? ""} onChange={(v) => setForm({ ...form, note: v })} />
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.active ?? true}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            Đang hoạt động
          </label>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm">
              {error?.response?.data?.detail ?? String(error)}
            </div>
          )}
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
            >
              Huỷ
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-60"
            >
              {saving ? "Đang lưu..." : "Lưu"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  required,
  textarea,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  textarea?: boolean;
}) {
  return (
    <div>
      <label className="block text-xs text-gray-600 mb-0.5">{label}</label>
      {textarea ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={2}
          className="w-full border rounded px-2 py-1.5 text-sm"
        />
      ) : (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          className="w-full border rounded px-2 py-1.5 text-sm"
        />
      )}
    </div>
  );
}
